// Owl Hour Tier Selector — five-tier picker.
// Each tab shows its scope and current publication availability.

import type { OwlTier, OwlHourSlip } from "../../owl/useOwlHourSlips.ts";
import { OWL_TIER_META } from "../../owl/useOwlHourSlips.ts";
import type { OwlUserSlip } from "../../owl/useOwlHourUserSlips.ts";

// ─── Tier order ───────────────────────────────────────────────

const TIERS: readonly OwlTier[] = [
  "rollover",
  "moderate",
  "conservative",
  "aggressive",
  "owl_1000",
];

// ─── Component ────────────────────────────────────────────────

export interface OwlTierSelectorProps {
  selectedTier: OwlTier;
  onSelect: (tier: OwlTier) => void;
  slips: readonly OwlHourSlip[];
  userSlips: readonly OwlUserSlip[];
  /** Per-tier locked stakes from the stake drawer */
  lockedStakes?: Partial<Record<OwlTier, number>>;
  unpricedSelected?: boolean;
  onSelectUnpriced?: () => void;
  unpricedCount?: number;
}

export function OwlTierSelector({
  selectedTier,
  onSelect,
  slips,
  lockedStakes = {},
  unpricedSelected = false,
  onSelectUnpriced,
  unpricedCount = 0,
}: OwlTierSelectorProps): JSX.Element {
  return (
    <div className="owl-tiers-tabs" aria-label="Select a prediction board">
      {TIERS.map((tier) => {
        const meta = OWL_TIER_META[tier];
        const isActive = !unpricedSelected && tier === selectedTier;
        const tonightSlip = slips.find((sl) => sl.tier === tier);

        const locked = lockedStakes[tier];

        return (
          <button
            key={tier}
            type="button"
            className={`owl-tier-tab${isActive ? " active" : ""}${locked !== undefined ? " locked" : ""}`}
            aria-pressed={isActive}
            onClick={() => onSelect(tier)}
          >
            <div className="lab">{meta.label}</div>
            <div className="name">{meta.name}</div>
            <div className="range">
              {meta.oddsRange} · {tonightSlip ? `${tonightSlip.legs.length} legs` : meta.legRange}
            </div>
            <div className="owl-tier-availability">
              {tonightSlip ? "Published" : "Not published"}
              {locked !== undefined ? " · Saved" : ""}
            </div>
          </button>
        );
      })}
      {onSelectUnpriced ? (
        <button
          type="button"
          className={`owl-tier-tab${unpricedSelected ? " active" : ""}`}
          onClick={onSelectUnpriced}
          aria-pressed={unpricedSelected}
        >
          <div className="lab">Read Only</div>
          <div className="name">Unpriced Picks</div>
          <div className="range">{unpricedCount} picks · Odds not verified</div>
        </button>
      ) : null}
    </div>
  );
}
